import { useCallback, useEffect, useRef, useState, type CSSProperties } from "react";
import { createPortal } from "react-dom";
import { ChevronDown, Search } from "lucide-react";
import { cn } from "../../lib/utils";

export interface DropdownOption<T extends string> { value: T; label: string }

interface DropdownProps<T extends string> {
  value: T;
  options: ReadonlyArray<DropdownOption<T>>;
  onChange: (value: T) => void;
  ariaLabel: string;
  className?: string;
  searchable?: boolean; // default: auto-on when > 8 options
  disabled?: boolean;
  size?: "sm" | "md";
  align?: "left" | "right";
  /** Force the menu to open downward even when space below looks tight. */
  dropDown?: boolean;
}

/**
 * Portal-based combobox: menu renders in <body>, so it can never be clipped
 * by modals/scroll containers. Flips up when there's no room below, caps its
 * height with internal scroll, and gains a type-ahead filter for long lists.
 * The menu starts invisible and fades in only after it has been positioned to
 * eliminate the first-open flicker (menu appearing at 0,0 before placement).
 */
export function Dropdown<T extends string>({
  value,
  options,
  onChange,
  ariaLabel,
  className,
  searchable,
  disabled,
  size = "md",
  align = "left",
  dropDown = false,
}: DropdownProps<T>) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [positioned, setPositioned] = useState(false);
  const btnRef = useRef<HTMLButtonElement>(null);
  const menuRef = useRef<HTMLDivElement>(null);
  const [style, setStyle] = useState<CSSProperties>({});

  const isSearchable = searchable ?? options.length > 8;
  const filtered = isSearchable && query
    ? options.filter((o) => o.label.toLowerCase().includes(query.toLowerCase()))
    : options;
  const current = options.find((o) => o.value === value);

  /* Position from the trigger's live rect; flip up if no room below */
  const place = useCallback(() => {
    const r = btnRef.current?.getBoundingClientRect();
    if (!r) return;
    const MAX_H = 300;
    const below = window.innerHeight - r.bottom;
    const up = !dropDown && below < MAX_H + 12 && r.top > below;
    const minW = size === "sm" ? 140 : 200;
    const w = Math.max(r.width, minW);

    const left = align === "right"
      ? Math.max(8, r.right - w)
      : Math.max(8, Math.min(r.left, window.innerWidth - w - 8));

    setStyle({
      position: "fixed",
      left,
      width: w,
      ...(up ? { bottom: window.innerHeight - r.top + 4 } : { top: r.bottom + 4 }),
      maxHeight: Math.min(MAX_H, (up ? r.top : below) - 12),
      zIndex: 9999,
    });
  }, [size, align, dropDown]);

  useEffect(() => {
    if (!open) {
      setPositioned(false);
      return;
    }
    setQuery("");
    // Measure immediately, then re-measure after the portal DOM settles.
    // Mark as positioned only after the second rAF so the menu is never
    // rendered at (0,0) — this eliminates the first-open flicker.
    place();
    const raf1 = requestAnimationFrame(() => {
      place();
      requestAnimationFrame(() => {
        place();
        setPositioned(true);
      });
    });
    /* capture=true catches scrolls inside modal bodies too */
    window.addEventListener("resize", place);
    window.addEventListener("scroll", place, true);
    return () => {
      cancelAnimationFrame(raf1);
      window.removeEventListener("resize", place);
      window.removeEventListener("scroll", place, true);
    };
  }, [open, place]);

  /* Outside click across portal boundary */
  useEffect(() => {
    if (!open) return;
    const onDown = (e: MouseEvent) => {
      const t = e.target as Node;
      if (!btnRef.current?.contains(t) && !menuRef.current?.contains(t)) setOpen(false);
    };
    document.addEventListener("mousedown", onDown);
    return () => document.removeEventListener("mousedown", onDown);
  }, [open]);

  const isSmall = size === "sm";

  return (
    <>
      <button
        ref={btnRef}
        type="button"
        disabled={disabled}
        aria-label={ariaLabel}
        aria-haspopup="listbox"
        aria-expanded={open}
        onClick={() => setOpen((o) => !o)}
        className={cn(
          "flex w-full items-center justify-between gap-2 rounded-lg border border-line dark:border-white/15 bg-soft dark:bg-stone-800 transition-colors hover:bg-line/60 dark:hover:bg-stone-700/60 focus:outline-none focus:ring-2 focus:ring-brand/30",
          isSmall ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm",
          "text-ink dark:text-stone-100",
          disabled && "cursor-not-allowed opacity-50",
          className,
        )}
      >
        <span className="truncate">{current?.label ?? "Select…"}</span>
        <ChevronDown
          className={cn(
            "shrink-0 text-ink-3 dark:text-stone-400 transition-transform",
            isSmall ? "h-3.5 w-3.5" : "h-4 w-4",
            open && "rotate-180",
          )}
        />
      </button>

      {open && createPortal(
        <div
          ref={menuRef}
          role="listbox"
          style={style}
          className={cn(
            "flex flex-col overflow-hidden rounded-xl border border-line dark:border-white/15 bg-elevated dark:bg-[#201e1b] shadow-xl transition-opacity duration-100",
            positioned ? "opacity-100" : "opacity-0 pointer-events-none",
          )}
        >
          {isSearchable && (
            <div className="relative border-b border-line dark:border-white/10">
              <Search className="pointer-events-none absolute left-3 top-1/2 h-3.5 w-3.5 -translate-y-1/2 text-ink-3 dark:text-stone-400" />
              <input
                autoFocus
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Type to filter…"
                className={cn(
                  "w-full bg-elevated dark:bg-[#201e1b] pl-9 pr-3 text-ink dark:text-stone-100 focus:outline-none placeholder:text-ink-3 dark:placeholder:text-stone-400",
                  isSmall ? "py-1.5 text-xs" : "py-2 text-sm",
                )}
              />
            </div>
          )}

          <ul className="flex-1 overflow-y-auto py-1">
            {filtered.length === 0 && (
              <li className="px-3 py-2 text-xs text-ink-3 dark:text-stone-400">No matches.</li>
            )}
            {filtered.map((opt) => (
              <li key={opt.value}>
                <button
                  type="button"
                  role="option"
                  aria-selected={opt.value === value}
                  onClick={() => { onChange(opt.value); setOpen(false); }}
                  className={cn(
                    "flex w-full items-center justify-between text-left transition-colors hover:bg-stone-100 dark:hover:bg-stone-800/80",
                    isSmall ? "px-2.5 py-1.5 text-xs" : "px-3 py-2 text-sm",
                    opt.value === value ? "font-semibold text-brand bg-brand/5 dark:bg-brand/10" : "text-ink-2 dark:text-stone-200",
                  )}
                >
                  <span className="truncate">{opt.label}</span>
                  {opt.value === value && (
                    <span className="ml-2 h-1.5 w-1.5 shrink-0 rounded-full bg-brand" />
                  )}
                </button>
              </li>
            ))}
          </ul>
        </div>,
        document.body,
      )}
    </>
  );
}
